import * as React from 'react';
import dynamic from 'next/dynamic';
import clsx from 'clsx';
import Grid from '@kiwicom/orbit-components/lib/utils/Grid';
import useIsMobileApp from '@/common/services/helpers/useIsMobileApp';
import { useFeature } from '@kiwicom/darwin';

import { PageLayoutContext } from './services/context/pageLayoutContext';
import Media from '../Media/Media';

const PageGridColumnWithFixedFooter = dynamic(
  () => import('./components/PageGridColumnWithFixedFooter'),
);

type Props = {
  header?: React.ReactElement;
  sidebar?: React.ReactElement;
  content?: React.ReactElement;
  footer?: React.ReactElement;
  hasFixedFooter?: boolean;
};

/**
 * Layout options for the page's grid based on screen-width:
 * - until 991 px: 1 column, 100% width
 * - 992-1199 px: 2 columns, left column takes auto width, right column takes width of sidebar's width with paddings
 * - from 1200 px: 2 columns, left column takes 60% of screen width, right column takes 40% of screen width. In case sidebar's width takes less than 40% of screen width, then sidebar's width applies for the right column width.
 */
type Options = {
  hasSidebar: boolean;
  hasSafeAreaInsetMargin: boolean;
};
const gridOptions = ({ hasSidebar, hasSafeAreaInsetMargin }: Options) => ({
  /* Max 100vw and overflow is needed for grid's content to not expand over the viewport width (e.g. long filename in DocumentVerification). */
  className: clsx(
    'h-fit min-h-full w-full max-w-[100vw] overflow-x-hidden font-base [&>:first-child]:max-w-[100vw]',
    hasSafeAreaInsetMargin && 'mt-[var(--safe-area-inset-top)]',
  ),
  columns: '1fr',
  desktop: {
    columns: hasSidebar
      ? 'auto calc(var(--mmb-page-sidebar-max-width) + 2 * var(--mmb-page-section-horizontal-padding-desktop))'
      : '1fr',
  },
  largeDesktop: {
    columns: hasSidebar
      ? '6fr minmax(calc(var(--mmb-page-sidebar-max-width) + 2 * var(--mmb-page-section-horizontal-padding-large-desktop)), 4fr)'
      : '1fr',
  },
});

/**
 * Page layout with header, sidebar, content and footer.
 * Sidebar is displayed only on desktop.
 * More information about the layout and its components: {@link https://kiwi.wiki/mmb/web-client/layout/}
 *
 * In order to get a 'responsive' sidebar (eg. Basket in PaymentPage), sidebar should
 * take a component wrapped inside a desktop-only media query and footer should take another
 * component wrapped inside a mobile-only media query, so that they get shown in the
 * appropriate location depending on screen size.
 *
 * Layout without fixed footer:
 *
 * ```jsx
 *   -- mobile --            -- desktop --
 * | <header />  |   | <header />  | <sidebar /> |
 * | <content /> |   | <content /> |     -       |
 * | <footer />  |   | <footer />  |     -       |
 * |     -       |   |     -       |     -       |
 * ```
 *
 * Layout with fixed footer:
 *
 * ```jsx
 *     -- mobile --                    -- desktop --
 * | <header />       |   | <header />       | <sidebar /> |
 * | <content />      |   | <content />      |     -       |
 * |     -            |   |     -            |     -       |
 * | <fixed footer /> |   | <fixed footer /> |     -       |
 * ```
 *
 * @param props - {@link Props}
 * @returns Page Layout component
 */
const PageLayout = ({
  header,
  sidebar,
  content,
  footer,
  hasFixedFooter = false,
}: Props): React.ReactElement => {
  const hasSidebar = Boolean(sidebar);
  const isMobileApp = useIsMobileApp();
  const safeAreaInsetAddedFeatureOn = useFeature('SET_AREA_INSET_ADDED');

  return (
    <PageLayoutContext.Provider value={{ hasSidebar, hasFixedFooter }}>
      <Grid
        {...gridOptions({
          hasSidebar,
          hasSafeAreaInsetMargin: safeAreaInsetAddedFeatureOn && isMobileApp,
        })}
      >
        {hasFixedFooter && footer ? (
          <PageGridColumnWithFixedFooter header={header} content={content} footer={footer} />
        ) : (
          <div>
            {header}
            {content}
            {footer}
          </div>
        )}
        {sidebar && (
          <Media greaterThanOrEqual="desktop">
            <aside
              className={clsx(
                'h-full bg-cloud-light',
                `px-[var(--mmb-page-section-horizontal-padding-desktop)] py-1200 ld:p-[var(--mmb-page-section-horizontal-padding-large-desktop)]`,
                `[&>*:first-child]:max-w-[var(--mmb-page-sidebar-max-width)]`,
              )}
            >
              {sidebar}
            </aside>
          </Media>
        )}
      </Grid>
    </PageLayoutContext.Provider>
  );
};

export default PageLayout;
